import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Tooltip, TablePagination, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Alert, Box,
} from '@mui/material';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AddIcon from '@mui/icons-material/Add';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import SearchFilterBar from '../../components/Common/SearchFilterBar';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import { getUsersApi, toggleUserStatusApi, adminCreateUserApi } from '../../api/auth';

const emptyForm = { name: '', email: '', mobile_number: '', password: '', confirm_password: '' };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(null);
  // Add User dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [addErrors, setAddErrors] = useState({});
  const [addNonFieldError, setAddNonFieldError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    const res = await getUsersApi({ page: page + 1, page_size: rowsPerPage, search });
    setUsers(res.data.results || res.data);
    setTotal(res.data.count || res.data.length);
  }, [page, rowsPerPage, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleToggle = async (id) => {
    await toggleUserStatusApi(id);
    fetchUsers();
    setConfirm(null);
  };

  const openAddUser = () => {
    setAddForm(emptyForm);
    setAddErrors({});
    setAddNonFieldError('');
    setAddSuccess('');
    setAddOpen(true);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddErrors({});
    setAddNonFieldError('');
    setAddSuccess('');
    try {
      const res = await adminCreateUserApi(addForm);
      setAddSuccess(res.data.message || 'User created successfully.');
      fetchUsers();
      setTimeout(() => { setAddOpen(false); setAddSuccess(''); }, 1500);
    } catch (err) {
      const data = err.response?.data || {};
      if (data.non_field_errors) {
        setAddNonFieldError(data.non_field_errors[0]);
      } else if (typeof data === 'string') {
        setAddNonFieldError(data);
      } else {
        setAddErrors(data);
      }
    } finally {
      setAddLoading(false);
    }
  };

  const setF = (k) => (e) => setAddForm({ ...addForm, [k]: e.target.value });

  return (
    <Layout title="End Users">
      <PageHeader
        title="End Users"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Users' }]}
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddUser}>
            Add User
          </Button>
        }
      />
      <Card>
        <CardContent>
          <SearchFilterBar searchPlaceholder="Search by name, email, mobile..." onSearch={setSearch} />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u, idx) => (
                <TableRow key={u.id} hover>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.mobile_number || '-'}</TableCell>
                  <TableCell>{new Date(u.date_joined).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip label={u.is_active ? 'Active' : 'Inactive'} color={u.is_active ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={u.is_active ? 'Deactivate' : 'Activate'}>
                      <IconButton size="small" color={u.is_active ? 'error' : 'success'}
                        onClick={() => setConfirm({ id: u.id, active: u.is_active, name: u.name })}>
                        {u.is_active ? <ToggleOffIcon /> : <ToggleOnIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div" count={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.active ? 'Deactivate User' : 'Activate User'}
        message={`Are you sure you want to ${confirm?.active ? 'deactivate' : 'activate'} ${confirm?.name}?`}
        onConfirm={() => handleToggle(confirm?.id)}
        onCancel={() => setConfirm(null)}
        confirmText={confirm?.active ? 'Deactivate' : 'Activate'}
      />

      {/* Add User dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent dividers>
          {addNonFieldError && <Alert severity="error" sx={{ mb: 2 }}>{addNonFieldError}</Alert>}
          {addSuccess && <Alert severity="success" sx={{ mb: 2 }}>{addSuccess}</Alert>}
          <Box component="form" id="add-user-form" onSubmit={handleAddUser} noValidate>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField fullWidth label="Full Name" value={addForm.name} onChange={setF('name')}
                  required error={!!addErrors.name} helperText={addErrors.name?.[0]} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Email ID" type="email" value={addForm.email} onChange={setF('email')}
                  required error={!!addErrors.email} helperText={addErrors.email?.[0]} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Mobile Number" value={addForm.mobile_number}
                  onChange={setF('mobile_number')} error={!!addErrors.mobile_number}
                  helperText={addErrors.mobile_number?.[0]} inputProps={{ maxLength: 15 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Password" type="password" value={addForm.password}
                  onChange={setF('password')} required error={!!addErrors.password}
                  helperText={addErrors.password?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Confirm Password" type="password" value={addForm.confirm_password}
                  onChange={setF('confirm_password')} required error={!!addErrors.confirm_password}
                  helperText={addErrors.confirm_password?.[0]} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button type="submit" form="add-user-form" variant="contained" disabled={addLoading}>
            {addLoading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
